import {
  Box,
  Drawer,
  Flex,
  Image,
  IconButton,
  Portal,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  CalendarPlus,
  CircleQuestionMark,
  Gift,
  History,
  House,
  LogIn,
  LogOut,
  Menu as MenuIcon,
  Package,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOutUser } from "../../services/AuthenticationService";
import { NavbarCTAButton } from "../Button";
import MyCartModal from "../Account/MyCartModal";
import SignOutConfirmDialog from "../Account/SignOutConfirmDialog";
import { useAuth } from "../../contexts/AuthProvider";
import { useMyCart } from "../../contexts/MyCartContext";
import { useRewardsReminder } from "../../contexts/RewardsReminderContext";
import { containerVariants, itemVariants } from "./NavDrawer/constants";
import { DrawerAccountAvatar } from "./NavDrawer/DrawerAccountAvatar";
import { DrawerNavItem } from "./NavDrawer/DrawerNavItem";
import { ServicesCollapsible } from "./NavDrawer/ServicesCollapsible";

const MotionDiv = motion.div;

export function NavDrawer({
  isAuthenticated,
  heroMode = false,
  visible,
}: {
  isAuthenticated: boolean;
  heroMode?: boolean;
  visible?: boolean;
}) {
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { count, whatsAppFailureCount } = useMyCart();
  const { hasUnseenReferralReward } = useRewardsReminder();
  const rewardsCount = hasUnseenReferralReward ? 1 : 0;
  const totalCount = (count ?? 0) + (whatsAppFailureCount ?? 0) + rewardsCount;
  const accountBadgeColor =
    whatsAppFailureCount > 0
      ? "#DC2626"
      : hasUnseenReferralReward
        ? "#2563EB"
        : "#EA580C";
  const fullName =
    `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Account";
  const initials = (
    (user?.first_name?.[0] ?? "") + (user?.last_name?.[0] ?? "")
  ).toUpperCase();
  const email = user?.email ?? "Personal Account";

  function handleSignOutClick() {
    setDrawerOpen(false);
    setShowSignOutConfirm(true);
  }

  async function handleConfirmSignOut() {
    setSigningOut(true);
    const { error } = await signOutUser();
    setSigningOut(false);

    if (!error) {
      setShowSignOutConfirm(false);
      navigate("/login");
    }
  }

  function handleNavigate(to: string) {
    navigate(to);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (visible === false && drawerOpen) {
      setDrawerOpen(false);
      window.dispatchEvent(new CustomEvent("hh:drawer-close"));
    }
  }, [drawerOpen, visible]);

  useEffect(() => {
    function handleCloseNavOverlays() {
      setDrawerOpen(false);
      window.dispatchEvent(new CustomEvent("hh:drawer-close"));
    }

    window.addEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
    return () => window.removeEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
  }, []);

  return (
    <>
      {showSignOutConfirm && (
        <SignOutConfirmDialog
          open={showSignOutConfirm}
          onConfirm={handleConfirmSignOut}
          onCancel={() => setShowSignOutConfirm(false)}
          loading={signingOut}
        />
      )}

      <Drawer.Root
        placement="end"
        trapFocus={false}
        open={drawerOpen}
        onOpenChange={(e) => {
          setDrawerOpen(e.open);
          window.dispatchEvent(new CustomEvent(e.open ? "hh:drawer-open" : "hh:drawer-close"));
        }}
      >
      <Drawer.Trigger asChild>
        <Flex
          position="relative"
          rounded="full"
          alignItems="center"
          justifyContent="center"
          w="2.75rem"
          h="2.75rem"
          cursor="pointer"
          border="1px solid"
          borderColor={
            heroMode ? "rgba(255,255,255,0.18)" : "greyscale.border.default"
          }
          color={heroMode ? "white" : "greyscale.text.body"}
          bg={heroMode ? "rgba(255,255,255,0.18)" : "white"}
          boxShadow={heroMode ? "0 2px 8px rgba(0,0,0,0.18)" : "0 10px 24px rgba(15,23,42,0.08)"}
          display={visible !== undefined ? (visible ? "flex" : "none") : ["flex", null, null, "none"]}
          aria-label="Open navigation menu"
          _hover={{
            bg: heroMode ? "rgba(255,255,255,0.22)" : "white",
            color: heroMode ? "white" : "primary.surface.default",
            transform: "translateY(-1px)",
          }}
          transition="all 0.2s ease"
        >
          <MenuIcon size={20} />
          {isAuthenticated && totalCount > 0 && (
            <Box
              position="absolute"
              top="-3px"
              right="-3px"
              minW="1.05rem"
              h="1.05rem"
              rounded="full"
              bg={accountBadgeColor}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="0.55rem"
              fontWeight="900"
              lineHeight="1"
              border="1.5px solid white"
              zIndex={1}
              px="0.2rem"
            >
              {totalCount}
            </Box>
          )}
        </Flex>
      </Drawer.Trigger>

      <Portal>
        <Drawer.Backdrop bg="rgba(15,23,42,0.48)" />
        <Drawer.Positioner>
          <Drawer.Content
            bg="white"
            borderTopLeftRadius="1.4rem"
            borderBottomLeftRadius="1.4rem"
            borderLeft="1px solid"
            borderColor="blackAlpha.100"
            boxShadow="0 24px 80px rgba(15,23,42,0.18)"
            overflow="hidden"
            w="min(20rem, calc(100vw - 1rem))"
            maxW="20rem"
            style={{ willChange: "transform" }}
          >
            <Drawer.Header
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              pt="1rem"
              pb="0.85rem"
            >
              <Drawer.Title>
                <Image
                  width="auto"
                  height="1.8rem"
                  src="/home/hhLogo2.png"
                  alt="HomeHelpy"
                  cursor="pointer"
                  transition="transform 0.22s ease, opacity 0.22s ease"
                  _hover={{ transform: "translateY(-1px)", opacity: 0.82 }}
                  _active={{ transform: "scale(0.97)" }}
                  onClick={() => handleNavigate("/")}
                />
              </Drawer.Title>
              <IconButton
                aria-label="Close navigation menu"
                size="sm"
                variant="ghost"
                rounded="full"
                color="#475569"
                _hover={{ bg: "rgba(15,23,42,0.06)", color: "#0F172A" }}
                onClick={() => setDrawerOpen(false)}
              >
                <X size={17} />
              </IconButton>
            </Drawer.Header>

            <Drawer.Body
              display="flex"
              flexDir="column"
              justifyContent="flex-start"
              gap="1rem"
              py="0.5rem"
            >
              <Box>
                <Text
                  px="0.9rem"
                  mb="0.5rem"
                  fontSize="0.72rem"
                  fontWeight="800"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.12em"
                >
                  Overview
                </Text>

                <MotionDiv
                  variants={containerVariants}
                  initial="hidden"
                  animate={drawerOpen ? "visible" : "exit"}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.35rem",
                  }}
                >
                  <MotionDiv variants={itemVariants}>
                    <DrawerNavItem
                      icon={<House size={18} />}
                      to="/"
                      name="Home"
                      isActive={path === "/"}
                      onNavigate={handleNavigate}
                    />
                  </MotionDiv>

                  <MotionDiv variants={itemVariants}>
                    <DrawerNavItem
                      icon={<CalendarPlus size={18} />}
                      to="/dashboard"
                      name="Book Now"
                      isActive={path === "/dashboard"}
                      onNavigate={handleNavigate}
                    />
                  </MotionDiv>

                  <MotionDiv variants={itemVariants}>
                    <DrawerNavItem
                      icon={<Package size={18} />}
                      to="/packages"
                      name="Packages"
                      isActive={path.startsWith("/packages")}
                      onNavigate={handleNavigate}
                    />
                  </MotionDiv>

                  <MotionDiv variants={itemVariants}>
                    <ServicesCollapsible
                      isActive={path.startsWith("/services")}
                      onNavigate={handleNavigate}
                    />
                  </MotionDiv>

                  <MotionDiv variants={itemVariants}>
                    <DrawerNavItem
                      icon={<CircleQuestionMark size={18} />}
                      to="/faq"
                      name="FAQ"
                      isActive={path.startsWith("/faq")}
                      onNavigate={handleNavigate}
                    />
                  </MotionDiv>
                </MotionDiv>
              </Box>

              {isAuthenticated && (
                <Box>
                  <Separator mt="0.5rem" mb="1.25rem" borderColor="blackAlpha.100" />

                  <Text
                    px="0.9rem"
                    mb="0.5rem"
                    fontSize="0.72rem"
                    fontWeight="800"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.12em"
                  >
                    Account
                  </Text>

                  <MotionDiv
                    variants={containerVariants}
                    initial="hidden"
                    animate={drawerOpen ? "visible" : "exit"}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.35rem",
                    }}
                  >
                    <MotionDiv variants={itemVariants}>
                      <DrawerNavItem
                        icon={<History size={19} />}
                        to="/my-bookings"
                        name="My Bookings"
                        description="Sessions, history, invoices"
                        isActive={path.startsWith("/my-bookings")}
                        onNavigate={handleNavigate}
                      />
                    </MotionDiv>

                    <MotionDiv variants={itemVariants}>
                      <DrawerNavItem
                        icon={<Package size={18} />}
                        to="/my-plans"
                        name="My Plans"
                        description="Packages & subscriptions"
                        isActive={path.startsWith("/my-plans")}
                        onNavigate={handleNavigate}
                      />
                    </MotionDiv>

                    <MotionDiv variants={itemVariants}>
                      <DrawerNavItem
                        icon={<Gift size={18} />}
                        to="/my-rewards"
                        name="My Rewards"
                        description="Vouchers and savings"
                        isActive={path.startsWith("/my-rewards")}
                        onNavigate={handleNavigate}
                        badge={rewardsCount}
                        badgeColor="#2563EB"
                      />
                    </MotionDiv>

                    <MotionDiv variants={itemVariants}>
                      <MyCartModal variant="drawer" />
                    </MotionDiv>
                  </MotionDiv>
                </Box>
              )}

              <MotionDiv
                initial={{ opacity: 0, y: 10 }}
                animate={
                  drawerOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 5 }
                }
                transition={{
                  duration: drawerOpen ? 0.4 : 0.2,
                  ease: "easeOut",
                  delay: drawerOpen ? 0.32 : 0,
                }}
                style={{ marginTop: "auto" }}
              >
                <Box>
                  {isAuthenticated ? (
                    <VStack align="stretch" gap="0">
                      <Flex
                        align="center"
                        gap="0.75rem"
                        px="0.9rem"
                        py="0.75rem"
                        mt="1.5rem"
                        rounded="0.85rem"
                        cursor="pointer"
                        _hover={{ bg: "rgba(34,197,94,0.06)" }}
                        transition="all 0.18s ease"
                        onClick={() => handleNavigate("/account")}
                      >
                        <DrawerAccountAvatar
                          initials={initials}
                          badge={totalCount > 0 ? totalCount : undefined}
                          badgeColor={accountBadgeColor}
                        />
                        <Box minW={0} flex="1">
                          <Text
                            fontSize="0.88rem"
                            fontWeight="700"
                            color="greyscale.text.title"
                            lineHeight="1.25"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {fullName}
                          </Text>
                          <Text
                            mt="0.05rem"
                            fontSize="0.73rem"
                            color="greyscale.text.caption"
                            lineHeight="1.35"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {email}
                          </Text>
                          <Text
                            mt="0.18rem"
                            fontSize="0.72rem"
                            fontWeight="750"
                            color="#15803D"
                            lineHeight="1.2"
                          >
                            View account settings
                          </Text>
                        </Box>
                      </Flex>
                      <Separator my="0.5rem" borderColor="blackAlpha.100" />
                      <Flex
                        px="0.95rem"
                        py="0.9rem"
                        rounded="0.85rem"
                        alignItems="center"
                        gap="0.75rem"
                        cursor="pointer"
                        color="#64748B"
                        border="1px solid transparent"
                        _hover={{
                          bg: "#FEF2F2",
                          borderColor: "rgba(220,38,38,0.12)",
                          color: "#DC2626",
                          transform: "translateX(2px)",
                          boxShadow: "0 6px 14px rgba(220,38,38,0.06)",
                        }}
                        transition="all 0.18s ease"
                        onClick={handleSignOutClick}
                      >
                        <Flex
                          w="1.55rem"
                          h="1.55rem"
                          align="center"
                          justify="center"
                          flexShrink={0}
                        >
                          <LogOut size={18} />
                        </Flex>
                        <Text fontSize="0.92rem" fontWeight="600">
                          Sign Out
                        </Text>
                      </Flex>
                    </VStack>
                  ) : (
                    <Box>
                      <NavbarCTAButton
                        scrolled={true}
                        onClick={() => handleNavigate("/login")}
                        icon={<LogIn size={14} />}
                        w="full"
                        h="2.9rem"
                        rounded="full"
                      >
                        Sign In
                      </NavbarCTAButton>
                    </Box>
                  )}
                </Box>
              </MotionDiv>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
      </Drawer.Root>
    </>
  );
}
